import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const AdminFeedbackTab = ({ user }) => {
  const { tokenManager } = useAuth();
  const [userResponses, setUserResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);

  useEffect(() => {
    if (user) {
      fetchUserResponses();
    }
  }, [user]);

  const fetchUserResponses = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Use the new efficient endpoint to get user responses directly
      const response = await fetch(`${API_URL}/surveys/user/${user._id || user.id}/responses?module=feedback`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const responses = data.responses || [];
        
        // Map responses to include question text from survey
        const mappedResponses = responses.map(response => {
          const mappedResponses = response.responses?.map((resp, index) => {
            const question = response.surveyId?.questions?.[index];
            
            return {
              question: question?.title || question?.name || `Question ${index + 1}`,
              answer: resp.answer || resp.value || 'No answer provided',
              questionType: question?.type || 'text',
              questionSection: question?.section || 'General'
            };
          }) || [];
          
          return {
            ...response,
            mappedResponses: mappedResponses
          };
        });
        
        setUserResponses(mappedResponses);
      } else {
        console.error('Failed to fetch user responses');
      }
    } catch (error) {
      console.error('Error fetching user responses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewResponse = (response) => {
    setSelectedResponse(response);
    setResponseDialogOpen(true);
  };

  const handleCloseResponseDialog = () => {
    setResponseDialogOpen(false);
    setSelectedResponse(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {user?.firstName} {user?.lastName}'s Feedback
      </Typography>
      
      {/* User's Survey Responses */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Survey Responses
          </Typography>
          
          {userResponses.length > 0 ? (
            <Grid container spacing={2}>
              {userResponses.map((response) => (
                <Grid item xs={12} md={6} key={response._id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <AssessmentIcon />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {response.surveyId?.title || 'Survey Response'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Completed: {new Date(response.submittedAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewResponse(response)}
                        sx={{
                          color: 'primary.main',
                          borderColor: 'primary.main',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            borderColor: 'primary.dark',
                          }
                        }}
                      >
                        View Response
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
          <Card>
              <CardContent>
              <Box textAlign="center" py={4}>
                <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Survey Responses Yet
                  </Typography>
                <Typography variant="body2" color="text.secondary">
                    {user?.firstName} {user?.lastName} has not responded to any feedback surveys yet.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

      {/* Response View Dialog */}
      <Dialog
        open={responseDialogOpen}
        onClose={handleCloseResponseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>
          Survey Response Details
          <IconButton
            aria-label="close"
            onClick={handleCloseResponseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedResponse && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {selectedResponse.surveyId?.title || 'Survey Response'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Completed by: {user?.firstName} {user?.lastName} on {new Date(selectedResponse.submittedAt).toLocaleDateString()}
              </Typography>
              
              <Divider sx={{ mb: 3 }} />
              
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Responses:
              </Typography>
              
              {selectedResponse.mappedResponses && selectedResponse.mappedResponses.length > 0 ? (
                <Box>
                  {(() => {
                    // Group responses by section
                    const groupedResponses = selectedResponse.mappedResponses.reduce((acc, response, index) => {
                      const section = response.questionSection || 'General';
                      if (!acc[section]) {
                        acc[section] = [];
                      }
                      acc[section].push({ ...response, originalIndex: index });
                      return acc;
                    }, {});

                    return Object.entries(groupedResponses).map(([sectionName, responses]) => (
                      <Box key={sectionName} sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2,ml: 2, color: 'primary.main' }}>
                          {sectionName}
                        </Typography>
                        
                        {responses.map((response, sectionIndex) => (
                          <Box key={response.originalIndex} sx={{ mb: 2, ml: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                              Question {response.originalIndex + 1}
                      </Typography>
                            
                            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                              {response.question}
                      </Typography>
                            
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                              Answer: {response.answer}
                      </Typography>
                          </Box>
                  ))}
                      </Box>
                    ));
                  })()}
                </Box>
              ) : (
                <Alert severity="info">
                  No response details available for this survey.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResponseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminFeedbackTab;
